import { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { z } from "zod";

import { useConfiguration, useUpdateConfiguration } from "@/api/configuration";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import SettingsCard from "./SettingsCard";

const TIMEZONE_OPTIONS = [
  "Europe/Berlin",
  "Europe/London",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Zurich",
  "Europe/Stockholm",
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Australia/Sydney",
  "UTC",
];

const WEEKDAY_KEYS = [
  { value: 1, key: "settings.configuration.company.weekdays.monday" },
  { value: 2, key: "settings.configuration.company.weekdays.tuesday" },
  { value: 3, key: "settings.configuration.company.weekdays.wednesday" },
  { value: 4, key: "settings.configuration.company.weekdays.thursday" },
  { value: 5, key: "settings.configuration.company.weekdays.friday" },
  { value: 6, key: "settings.configuration.company.weekdays.saturday" },
  { value: 7, key: "settings.configuration.company.weekdays.sunday" },
];

const schema = z.object({
  currencyCode: z
    .string()
    .regex(/^[A-Z]{3}$/, "Must be a 3-letter ISO code (e.g. EUR)"),
  timezone: z.string().min(1, "Required"),
  weekStart: z.number().int().min(1).max(7),
});

type FormValues = z.infer<typeof schema>;

export default function CompanySettings() {
  const { t } = useTranslation();
  const configQuery = useConfiguration();
  const update = useUpdateConfiguration();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      currencyCode: "EUR",
      timezone: "Europe/Berlin",
      weekStart: 1,
    },
  });

  useEffect(() => {
    if (configQuery.data) {
      form.reset({
        currencyCode: configQuery.data.currencyCode,
        timezone: configQuery.data.timezone,
        weekStart: configQuery.data.weekStart,
      });
    }
  }, [configQuery.data, form]);

  const onSubmit = (values: FormValues) => {
    update.mutate(values, {
      onSuccess: () =>
        toast.success(t("settings.saved", { defaultValue: "Saved" })),
      onError: () =>
        toast.error(t("settings.saveFailed", { defaultValue: "Save failed" })),
    });
  };

  return (
    <SettingsCard
      title={t("settings.rail.company")}
      description={t("settings.configuration.company.description")}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="currencyCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.configuration.company.currencyLabel")}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value.toUpperCase())
                    }
                    maxLength={3}
                    className="w-24 font-mono"
                  />
                </FormControl>
                <FormDescription>
                  {t("settings.configuration.company.currencyDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.configuration.company.timezoneLabel")}
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-64">
                      <SelectValue
                        placeholder={t(
                          "settings.configuration.company.timezonePlaceholder",
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  {t("settings.configuration.company.timezoneDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="weekStart"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.configuration.company.weekStartLabel")}
                </FormLabel>
                <Select
                  onValueChange={(v) => field.onChange(parseInt(v, 10))}
                  value={String(field.value)}
                >
                  <FormControl>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {WEEKDAY_KEYS.map((d) => (
                      <SelectItem key={d.value} value={String(d.value)}>
                        {t(d.key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  {t("settings.configuration.company.weekStartDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!form.formState.isDirty || update.isPending}
            >
              {update.isPending
                ? t("settings.configuration.saving")
                : t("common.buttons.save")}
            </Button>
          </div>
        </form>
      </Form>
    </SettingsCard>
  );
}
